// Mobile-friendly diagnostic banner. When the URL has `?debug=1`,
// captures uncaught errors + unhandled promise rejections + console
// errors and pins them to a fixed-position banner at the top of the
// page. Lets us read full stack traces on a phone without Web
// Inspector (which Linux can't drive on iOS).
//
// Specifically built to surface React hydration mismatches: the
// React error message includes the diff between server and client
// HTML, but iOS Safari console truncates long messages.
//
// Strip after debugging or leave shipped -- the banner is invisible
// without the query param. Zero perf cost without the flag.
import * as React from "react";

interface CapturedEvent {
  ts: number;
  kind: "error" | "rejection" | "console";
  message: string;
  stack?: string;
}

// Find the first byte that differs between SSR and live HTML, return
// a context window of ~120 chars on each side. Strips React-internal
// markers (<!--$-->, <!--/$-->, hydration boundary IDs) which
// legitimately differ but aren't real mismatches.
function firstDiff(ssr: string, live: string): string | null {
  const cleanSSR = ssr
    .replace(/<!--\$\??-->/g, "")
    .replace(/<!--\/\$-->/g, "")
    .replace(/<!--B:\d+-->/g, "")
    .replace(/<template id="B:\d+"><\/template>/g, "");
  const cleanLive = live
    .replace(/<!--\$\??-->/g, "")
    .replace(/<!--\/\$-->/g, "")
    .replace(/<!--B:\d+-->/g, "")
    .replace(/<template id="B:\d+"><\/template>/g, "");
  let i = 0;
  const minLen = Math.min(cleanSSR.length, cleanLive.length);
  while (i < minLen && cleanSSR[i] === cleanLive[i]) i++;
  if (i === minLen && cleanSSR.length === cleanLive.length) return null;
  const start = Math.max(0, i - 100);
  const ssrWindow = cleanSSR.slice(start, i + 100);
  const liveWindow = cleanLive.slice(start, i + 100);
  return (
    "@" +
    i +
    "\nSSR : ..." +
    ssrWindow +
    "...\nLIVE: ..." +
    liveWindow +
    "..."
  );
}

export function HydrationDebugBanner() {
  const [enabled, setEnabled] = React.useState(false);
  const [events, setEvents] = React.useState<CapturedEvent[]>([]);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("debug") !== "1") return;
    setEnabled(true);

    const push = (e: CapturedEvent) =>
      setEvents((prev) => [...prev, e].slice(-20));

    const onError = (ev: ErrorEvent) => {
      push({
        ts: Date.now(),
        kind: "error",
        message: ev.message ?? String(ev.error),
        stack: ev.error?.stack,
      });
    };
    const onRejection = (ev: PromiseRejectionEvent) => {
      const reason = ev.reason;
      push({
        ts: Date.now(),
        kind: "rejection",
        message:
          typeof reason === "string"
            ? reason
            : reason?.message ?? JSON.stringify(reason),
        stack: reason?.stack,
      });
    };

    const origConsoleError = console.error;
    console.error = (...args: unknown[]) => {
      origConsoleError.apply(console, args);
      const message = args
        .map((a) =>
          a instanceof Error
            ? a.message
            : typeof a === "string"
            ? a
            : (() => {
                try {
                  return JSON.stringify(a);
                } catch {
                  return String(a);
                }
              })(),
        )
        .join(" ");
      const stack = args.find((a) => a instanceof Error)?.stack;
      push({ ts: Date.now(), kind: "console", message, stack });

      // On any hydration-related error, grab a snippet of the diff
      // between the SSR HTML (captured pre-hydration in root.tsx)
      // and the current DOM (post-iOS-mutation). Trim aggressively
      // so it fits on a phone screen.
      if (/hydrat/i.test(message)) {
        try {
          const ssr: string | undefined = (window as any).__manosSSRBody;
          if (ssr && document.body) {
            const live = document.body.outerHTML;
            const diff = firstDiff(ssr, live);
            if (diff) {
              push({
                ts: Date.now(),
                kind: "console",
                message: "[SSR-vs-LIVE DIFF] " + diff,
              });
            }
          }
        } catch {}
      }
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      console.error = origConsoleError;
    };
  }, []);

  if (!enabled || events.length === 0) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        maxHeight: "60vh",
        overflowY: "auto",
        zIndex: 99999,
        background: "rgba(127, 29, 29, 0.97)",
        color: "#fff",
        fontFamily: "ui-monospace, monospace",
        fontSize: "11px",
        lineHeight: "1.4",
        padding: "8px 10px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "6px",
          fontWeight: 700,
        }}
      >
        <span>HYDRATION DEBUG ({events.length})</span>
        <button
          onClick={() => setEvents([])}
          style={{
            background: "rgba(255,255,255,0.2)",
            color: "#fff",
            border: "0",
            padding: "2px 8px",
            fontSize: "11px",
            borderRadius: "3px",
          }}
        >
          clear
        </button>
      </div>
      {events.map((e, i) => (
        <div
          key={i}
          style={{
            borderTop: "1px solid rgba(255,255,255,0.2)",
            paddingTop: "4px",
            marginTop: "4px",
            wordBreak: "break-word",
            whiteSpace: "pre-wrap",
          }}
        >
          <div style={{ opacity: 0.7 }}>
            [{e.kind}] {new Date(e.ts).toLocaleTimeString()}
          </div>
          <div>{e.message}</div>
          {e.stack ? (
            <div style={{ opacity: 0.6, fontSize: "10px" }}>{e.stack}</div>
          ) : null}
        </div>
      ))}
    </div>
  );
}
